export default class MixerScheduler {
  constructor(initNumPlayers, queueMatrix = [], removedUsersIndices = []) {
    this.numPlayers = initNumPlayers;
    this.queueMatrix = queueMatrix;
    this.removedParticipants = new Set(removedUsersIndices); // Track removed participant indices
  }

  generateSchedule(prioritizedPairs = []) {
    prioritizedPairs = prioritizedPairs || [];
    // Reset the removed participants tracking
    this.removedParticipants = new Set();

    const numPlayers = this.numPlayers;
    const totalPlayers = numPlayers; // No dummy player for initial generation

    // Create a map of prioritized pairs with input order index for quick lookup
    const prioritizedPairsMap = new Map();
    for (let i = 0; i < prioritizedPairs.length; i++) {
      const [p1, p2] = prioritizedPairs[i];
      const key = `${Math.min(p1, p2)}-${Math.max(p1, p2)}`;
      if (!prioritizedPairsMap.has(key)) {
        prioritizedPairsMap.set(key, i);
      }
    }

    // Generate all possible matches
    const remainingMatches = [];
    for (let i = 0; i < numPlayers; i++) {
      for (let j = i + 1; j < numPlayers; j++) {
        remainingMatches.push([i, j]);
      }
    }

    // Initialize participation counts
    const participationCounts = Array(totalPlayers).fill(0);

    // Active participants are all players initially
    const activeParticipants = [];
    for (let i = 0; i < numPlayers; i++) {
      activeParticipants.push(i);
    }

    // Schedule rounds using the common logic
    const schedule = this._scheduleRounds([], remainingMatches, participationCounts, prioritizedPairsMap, totalPlayers, activeParticipants);

    this.queueMatrix = schedule;
    return schedule;
  }

  _scheduleRounds(initialRounds, remainingMatches, participationCounts, prioritizedPairsMap, totalPlayers, activeParticipants) {
    const updatedSchedule = initialRounds.slice();

    while (remainingMatches.length > 0) {
      const roundMatches = {};
      const scheduledPlayers = new Set();

      let availableMatches = [...remainingMatches];
      let madeChange = true;

      while (madeChange) {
        madeChange = false;
        let bestMatchIndex = -1;
        let minScore = Infinity;

        for (let i = 0; i < availableMatches.length; i++) {
          const [p1, p2] = availableMatches[i];
          if (!scheduledPlayers.has(p1) && !scheduledPlayers.has(p2)) {
            let score = participationCounts[p1] + participationCounts[p2];
            const pairKey = `${Math.min(p1, p2)}-${Math.max(p1, p2)}`;
            const priority = prioritizedPairsMap.get(pairKey);
            if (priority !== undefined) {
              score -= (1000 - priority); // Prioritize lower index (earlier in input array)
            }
            if (score < minScore) {
              minScore = score;
              bestMatchIndex = i;
            }
          }
        }

        if (bestMatchIndex !== -1) {
          const [p1, p2] = availableMatches[bestMatchIndex];

          roundMatches[p1] = p2;
          roundMatches[p2] = p1;
          scheduledPlayers.add(p1);
          scheduledPlayers.add(p2);

          const originalIndex = remainingMatches.findIndex(match =>
            (match[0] === p1 && match[1] === p2) || (match[0] === p2 && match[1] === p1)
          );
          if (originalIndex !== -1) {
            remainingMatches.splice(originalIndex, 1);
          }
          availableMatches.splice(bestMatchIndex, 1);

          madeChange = true;
        }
      }

      for (let i = 0; i < activeParticipants.length; i++) {
        const playerIndex = activeParticipants[i];
        if (!scheduledPlayers.has(playerIndex)) {
          roundMatches[playerIndex] = null;
        }
      }

      const roundArray = Array(totalPlayers).fill(null);

      this.removedParticipants.forEach(removedIdx => {
        if (removedIdx < totalPlayers) {
          roundArray[removedIdx] = null;
        }
      });

      for (let player = 0; player < totalPlayers; player++) {
        if (roundMatches.hasOwnProperty(player)) {
          roundArray[player] = roundMatches[player];
        }
      }

      if (!roundArray.every((match) => match === null)) {
        updatedSchedule.push(roundArray);

        for (let player = 0; player < totalPlayers; player++) {
          const opponent = roundArray[player];
          if (opponent !== null && player < opponent) {
            participationCounts[player]++;
            participationCounts[opponent]++;
          }
        }
      } else if (remainingMatches.length > 0) {
        console.warn("Generated an all-null round while matches still remained.");
        break;
      }
    }

    return updatedSchedule;
  }

  updateScheduleWithNewParticipants(roundNumber, newParticipants, prioritizedPairs = []) {
    prioritizedPairs = prioritizedPairs || [];
    const initialSchedule = this.queueMatrix;
    const totalPlayers = this.numPlayers + newParticipants.length;

    const expandedSchedule = initialSchedule.map((round) => {
      const expandedRound = round.slice();
      for (let i = this.numPlayers; i < totalPlayers; i++) {
        expandedRound.push(null);
      }
      return expandedRound;
    });

    const matchesPlayed = new Set();
    for (let round = 0; round < roundNumber; round++) {
      for (let player = 0; player < this.numPlayers; player++) {
        const opponent = expandedSchedule[round][player];
        if (opponent !== null && player < opponent) {
          matchesPlayed.add(`${player}-${opponent}`);
        }
      }
    }

    // Create a list of remaining active participants (excluding removed ones)
    const activeParticipants = [];
    for (let i = 0; i < this.numPlayers; i++) {
      if (!this.removedParticipants.has(i)) {
        activeParticipants.push(i);
      }
    }

    // Add the new participants (they are always active)
    for (let i = this.numPlayers; i < totalPlayers; i++) {
      activeParticipants.push(i);
    }

    // Determine remaining matches only among active participants
    const remainingMatches = [];
    for (let i = 0; i < activeParticipants.length; i++) {
      for (let j = i + 1; j < activeParticipants.length; j++) {
        const p1 = activeParticipants[i];
        const p2 = activeParticipants[j];
        const matchKey = `${Math.min(p1, p2)}-${Math.max(p1, p2)}`;
        if (!matchesPlayed.has(matchKey)) {
          remainingMatches.push([p1, p2]);
        }
      }
    }

    const updatedSchedule = expandedSchedule.slice(0, roundNumber);
    // Initialize participation counts for the new rounds
    const participationCounts = Array(totalPlayers).fill(0);

    // Create a map of prioritized pairs with input order index for quick lookup
    const prioritizedPairsMap = new Map();
    for (let i = 0; i < prioritizedPairs.length; i++) {
      const [p1, p2] = prioritizedPairs[i];
      const key = `${Math.min(p1, p2)}-${Math.max(p1, p2)}`;
      if (!prioritizedPairsMap.has(key)) {
        prioritizedPairsMap.set(key, i);
      }
    }

    // Schedule future rounds using the common logic
    const finalSchedule = this._scheduleRounds(updatedSchedule, remainingMatches, participationCounts, prioritizedPairsMap, totalPlayers, activeParticipants);

    this.queueMatrix = finalSchedule;
    this.numPlayers = totalPlayers;

    return finalSchedule;
  }

  removeParticipantsFromSchedule(roundNumber, participantsToRemoveIndices) {
    if (!participantsToRemoveIndices || participantsToRemoveIndices.length === 0) {
      console.warn("No participants specified for removal.");
      return this.queueMatrix; // Return original matrix if no one is removed
    }

    const originalSchedule = this.queueMatrix;
    const originalNumPlayers = this.numPlayers;

    // Ensure indices are valid
    const validIndicesToRemove = participantsToRemoveIndices.filter(
      (index) => index >= 0 && index < (originalNumPlayers || originalSchedule.length)
    );
    if (validIndicesToRemove.length !== participantsToRemoveIndices.length) {
      console.warn("Some invalid participant indices provided for removal.");
    }
    const removalSet = new Set(validIndicesToRemove);

    // Update the internal set of removed participants
    validIndicesToRemove.forEach(idx => {
      this.removedParticipants.add(idx);
    });

    if (roundNumber < 0 || roundNumber > originalSchedule.length) {
      console.error(
        `Invalid round number for removal: ${roundNumber}. Must be between 0 and ${originalSchedule.length}.`
      );
      return this.queueMatrix; // Return original on invalid round
    }

    // 1. Preserve Past Rounds
    const preservedSchedule = originalSchedule.slice(0, roundNumber);

    // 2. Identify Played Matches (up to the removal round)
    const matchesPlayed = new Set();
    for (let round = 0; round < roundNumber; round++) {
      for (let player = 0; player < originalNumPlayers; player++) {
        // Only check players present in this round's data
        if (originalSchedule[round] && originalSchedule[round][player] !== undefined) {
          const opponent = originalSchedule[round][player];
          // Ensure the pair is added only once and involves valid players
          if (opponent !== null && player < opponent) {
            matchesPlayed.add(`${player}-${opponent}`);
          }
        }
      }
    }

    // 3. Identify Remaining Active Participants
    const remainingParticipantsIndices = [];
    for (let i = 0; i < originalNumPlayers; i++) {
      // Check both the current removalSet and the class's removedParticipants set
      if (!removalSet.has(i) && !this.removedParticipants.has(i)) {
        remainingParticipantsIndices.push(i);
      }
    }
    const numRemainingPlayers = remainingParticipantsIndices.length;

    // 4. Determine Potential Future Matches (among remaining players)
    const potentialFutureMatches = [];
    for (let i = 0; i < numRemainingPlayers; i++) {
      for (let j = i + 1; j < numRemainingPlayers; j++) {
        const p1 = remainingParticipantsIndices[i];
        const p2 = remainingParticipantsIndices[j];
        const matchKey1 = `${p1}-${p2}`;
        const matchKey2 = `${p2}-${p1}`; // Check both orders

        // Only consider matches NOT played before the removal round
        if (!matchesPlayed.has(matchKey1) && !matchesPlayed.has(matchKey2)) {
          potentialFutureMatches.push([p1, p2]); // Store original indices
        }
      }
    }

    // 5. Reschedule Future Rounds
    const futureSchedule = [];
    let remainingMatchesToSchedule = [...potentialFutureMatches];
    // Participation counts ONLY for remaining players within the rescheduled rounds
    const participationCounts = {};
    remainingParticipantsIndices.forEach(idx => participationCounts[idx] = 0);


    while (remainingMatchesToSchedule.length > 0) {
      const roundMatches = {}; // Stores matches for the current future round { P_idx: Opponent_idx }
      const scheduledPlayersThisRound = new Set(); // Tracks original indices scheduled

      let availableMatchesForRound = [...remainingMatchesToSchedule];
      let madeChange = true;

      while (madeChange && scheduledPlayersThisRound.size < numRemainingPlayers) {
        madeChange = false;
        let bestMatchIndex = -1; // Index in availableMatchesForRound
        let minScore = Infinity;

        // Find the best match among available remaining players
        for (let i = 0; i < availableMatchesForRound.length; i++) {
          const [p1, p2] = availableMatchesForRound[i]; // Original indices
          if (!scheduledPlayersThisRound.has(p1) && !scheduledPlayersThisRound.has(p2)) {
            // Score based on participation count of remaining players
            const score = (participationCounts[p1] ?? 0) + (participationCounts[p2] ?? 0);
            if (score < minScore) {
              minScore = score;
              bestMatchIndex = i;
            }
          }
        }

        if (bestMatchIndex !== -1) {
          const [p1, p2] = availableMatchesForRound[bestMatchIndex];

          // Schedule the match using original indices
          roundMatches[p1] = p2;
          roundMatches[p2] = p1;
          scheduledPlayersThisRound.add(p1);
          scheduledPlayersThisRound.add(p2);

          // Remove from the master remaining list
          const originalMasterIndex = remainingMatchesToSchedule.findIndex(
            (match) => (match[0] === p1 && match[1] === p2) || (match[0] === p2 && match[1] === p1)
          );
          if (originalMasterIndex !== -1) {
            remainingMatchesToSchedule.splice(originalMasterIndex, 1);
          }
          // Remove from the temporary list for this round's processing
          availableMatchesForRound.splice(bestMatchIndex, 1);

          madeChange = true; // Continue finding matches for this round
        }
      } // End inner while (finding matches for the round)

      // Assign byes to remaining players who weren't scheduled
      remainingParticipantsIndices.forEach(playerIndex => {
        if (!scheduledPlayersThisRound.has(playerIndex)) {
          roundMatches[playerIndex] = null; // Assign bye using original index
        }
      });


      // Construct the round array, maintaining original player slots
      const roundArray = Array(originalNumPlayers).fill(null); // Start with null values for everyone

      // Assign matches for remaining active participants
      for (let player = 0; player < originalNumPlayers; player++) {
        // Only process active participants (not in removal set and not previously removed)
        if (!removalSet.has(player) && !this.removedParticipants.has(player) && roundMatches.hasOwnProperty(player)) {
          // This is an active player with a match or bye assignment
          roundArray[player] = roundMatches[player];
        }
      }

      // Determine if this round contains any assignments (matches or byes)
      // for the players still active in the rescheduling process.
      const activityThisRound = remainingParticipantsIndices.some(
        idx => roundMatches.hasOwnProperty(idx)
      );

      // Add the round if there was activity (matches or byes assigned).
      // Avoid adding if no activity and no remaining matches.
      if (activityThisRound) { // Simpler: Add if any remaining player got an assignment (match or bye)
        if (roundArray.length !== originalNumPlayers) {
          console.error("Error: Rescheduled round length mismatch!");
          // Handle error state, maybe break or return partial result
          break;
        }
        futureSchedule.push(roundArray);

        // Update participation counts for players who had a match this round
        scheduledPlayersThisRound.forEach(playerIndex => {
          const opponent = roundMatches[playerIndex];
          // Increment count only if they played (opponent not null)
          // Count each match once (e.g., when player < opponent)
          if (opponent !== null && playerIndex < opponent) {
            participationCounts[playerIndex] = (participationCounts[playerIndex] ?? 0) + 1;
            participationCounts[opponent] = (participationCounts[opponent] ?? 0) + 1;
          }
        });

      } else if (remainingMatchesToSchedule.length > 0) {
        // Avoid infinite loops if scheduling fails to place remaining matches
        // This case should ideally not happen if activityThisRound captures all assignments correctly.
        console.warn("Generated a round with no activity while matches still remained. Stopping rescheduling.");
        break;
      }
    } // End outer while (remainingMatchesToSchedule.length > 0)


    // 6. Combine & Update
    const finalSchedule = preservedSchedule.concat(futureSchedule);

    // Update internal state - IMPORTANT: numPlayers should remain the original count
    // because the matrix dimensions are based on it. The *concept* of active players changes,
    // but the data structure holds slots for everyone for consistency.
    this.queueMatrix = finalSchedule;
    // this.numPlayers = originalNumPlayers; // Keep original numPlayers

    return finalSchedule;
  }

  removeDuplicateMeetings(flattenedMatrix) {
    // Step 1: Convert matrix entries to a more manageable format  
    const meetings = Object.entries(flattenedMatrix).map(([key, value]) => {
      const [round, user1] = key.split('_');
      return {
        round: parseInt(round),
        pair: [parseInt(user1), parseInt(value)].sort((a, b) => a - b) // Sort to normalize pair order  
      };
    });

    const areDuplicatePairs = (pair1, pair2) => {
      const [user1A, user1B] = pair1;
      const [user2A, user2B] = pair2;
      return (user1A === user2B && user1B === user2A) || (user1A === user2A && user1B === user2B);
    };

    // Step 2: Group meetings by round  
    const meetingsByRound = meetings.reduce((acc, meeting) => {
      if (!acc[meeting.round]) {
        acc[meeting.round] = [];
      }
      acc[meeting.round].push(meeting.pair);
      return acc;
    }, {});

    // Step 3: Remove duplicates within each round  
    const uniqueMeetings = {};
    Object.entries(meetingsByRound).forEach(([round, pairs]) => {
      const uniquePairs = pairs.filter((pair, index) => {
        const isUnique = !pairs.some((otherPair, otherIndex) =>
          index > otherIndex && areDuplicatePairs(pair, otherPair)
        );
        return isUnique;
      });

      // Step 4: Convert back to original format  
      uniquePairs.forEach(([user1, user2]) => {
        uniqueMeetings[`${round}_${user1}`] = user2;
      });
    });

    return uniqueMeetings;
  }
}